import { API } from "@swivl-mono/api-helper";
import { VarsHelper } from "@swivl-mono/helpers";
import { AppType, LocationVarInheritance, LocationVarInterface, LocationVarKey, VarDef, VarType } from "@swivl-mono/types";
import { Select, SelectOption, SelectSize } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { SettingRow, SettingsCard } from "../../../Components/SettingsCard/SettingsCard";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import AppsModel from "../../../Models/Apps/Apps.Model";
import { Member } from "../../../Models/Member/Member.Current";
import ReviewsModel from "../../../Models/Reviews/Reviews.Model";
import { revertLabelFor } from "../../../Views/Properties/CortexV3/Components/revertTarget";
import { WidgetSwitch } from "../../../Views/Widgets/WidgetSwitch";

/** Google Reviews page — sync + auto-reply settings. Not a Cortex topic (no bot impact). */

export const PAGE_KEYS: LocationVarKey[] = [
  LocationVarKey.GoogleReviewsSyncEnabled,
  LocationVarKey.GoogleAppId,
  LocationVarKey.GoogleAccountAndLocationName,
  LocationVarKey.GoogleReviewsAutoReplyEnabled,
  LocationVarKey.GoogleReviewsAutoReplyMinRating
];

type ScopeKind = "org" | "group" | "location";

/** The built-in default for a var, the same source the server merges from. */
function varDefault(varKey: LocationVarKey): string {
  return VarDef[VarsHelper.varKeyForType(VarType.Location, varKey)]?.defaultValue ?? "";
}

interface Resolved {
  /** Scope owning the winning row, or undefined when nothing is set anywhere. */
  ownerScope: ScopeKind | undefined;
  /** The row at the CURRENT scope, if any — what a revert would delete. */
  ownRow: LocationVarInterface | null;
  value: string;
  /** What would win if the current scope's row were deleted. */
  parentScope: ScopeKind | undefined;
  parentValue: string;
}

/** Walk location → group → org; report the winning scope and its runner-up. */
function resolveCascade(
  rows: LocationVarInterface[],
  scopeKind: ScopeKind,
  groupId: number | undefined,
  locationId: number | undefined,
  fallback: string
): Resolved {
  const orgRow = rows.find((r) => (r.groupId ?? null) === null && (r.locationId ?? null) === null) ?? null;
  const groupRow = groupId != null ? (rows.find((r) => (r.locationId ?? null) === null && r.groupId === groupId) ?? null) : null;
  const locationRow = locationId != null ? (rows.find((r) => r.locationId === locationId) ?? null) : null;

  const chain: [ScopeKind, LocationVarInterface | null][] =
    scopeKind === "location"
      ? [
          ["location", locationRow],
          ["group", groupRow],
          ["org", orgRow]
        ]
      : scopeKind === "group"
        ? [
            ["group", groupRow],
            ["org", orgRow]
          ]
        : [["org", orgRow]];

  const winner = chain.find(([, row]) => !!row?.value);
  const parent = chain.slice(1).find(([, row]) => !!row?.value);

  return {
    ownerScope: winner?.[0],
    ownRow: chain[0]![1],
    value: winner?.[1]?.value ?? fallback,
    parentScope: parent?.[0],
    parentValue: parent?.[1]?.value ?? fallback
  };
}

/** Map a resolved scope onto the shared inheritance vocabulary. */
function inheritanceFor(ownerScope: ScopeKind | undefined, scopeKind: ScopeKind): LocationVarInheritance {
  if (ownerScope === undefined) return LocationVarInheritance.DEFAULT;
  if (ownerScope === scopeKind) return LocationVarInheritance.NONE;
  return ownerScope === "group" ? LocationVarInheritance.GROUP : LocationVarInheritance.ORG;
}

export default function GoogleReviewsPage(): React.ReactElement {
  const page = usePropertiesGetLocationIds();
  const orgId = Member.orgId;
  const scopeKind: ScopeKind = page.locationId != null ? "location" : page.groupId != null ? "group" : "org";

  const [rowsByKey, setRowsByKey] = useState<Partial<Record<LocationVarKey, LocationVarInterface[]>>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const results = await Promise.all(PAGE_KEYS.map((key) => API.LocationVars.getAllVariablesForKey(orgId, key)));
      const map: Partial<Record<LocationVarKey, LocationVarInterface[]>> = {};
      PAGE_KEYS.forEach((key, i) => {
        map[key] = results[i];
      });
      setRowsByKey(map);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, [orgId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  if (!orgId) return <div className="p-6 text-sm text-grey-500">No active org.</div>;

  return (
    <div className="mx-auto flex max-w-[960px] flex-col gap-4 p-6">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Google Reviews</h2>
          </div>
          <p className="pl-3 text-base text-grey-600">Sync reviews from Google and let the bot auto-reply.</p>
        </div>
      </header>

      {loadError && <p className="body-sm rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">Failed: {loadError}</p>}

      <GoogleReviewsCard
        orgId={orgId}
        rowsByKey={rowsByKey}
        scopeKind={scopeKind}
        groupId={page.groupId}
        locationId={page.locationId}
        scopeTitle={page.typeTitle}
        onSaved={load}
      />
    </div>
  );
}

// ─── Google Reviews card ─────────────────────────────────────────────────────

interface GoogleReviewsCardProps {
  orgId: number;
  rowsByKey: Partial<Record<LocationVarKey, LocationVarInterface[]>>;
  scopeKind: ScopeKind;
  groupId: number | undefined;
  locationId: number | undefined;
  scopeTitle: string;
  onSaved: () => Promise<void>;
}

function isTruthy(value: string | undefined): boolean {
  return value === "1" || value === "true";
}

function GoogleReviewsCard({ orgId, rowsByKey, scopeKind, groupId, locationId, scopeTitle, onSaved }: GoogleReviewsCardProps): React.ReactElement {
  const resolvedByKey: Partial<Record<LocationVarKey, Resolved>> = {};
  for (const key of PAGE_KEYS) {
    resolvedByKey[key] = resolveCascade(rowsByKey[key] ?? [], scopeKind, groupId, locationId, varDefault(key));
  }

  const [draft, setDraft] = useState<Partial<Record<LocationVarKey, string>>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const display = (key: LocationVarKey): string => draft[key] ?? resolvedByKey[key]!.value;

  const { apps } = AppsModel.useState((s) => s);
  const { googleLocationsForApp } = ReviewsModel.useState((s) => s);

  useEffect(() => {
    if (!apps) AppsModel.load(false);
  }, [apps]);

  const currentGoogleAppId = display(LocationVarKey.GoogleAppId);

  useEffect(() => {
    if (currentGoogleAppId) ReviewsModel.loadGoogleLocationsForApp(parseInt(currentGoogleAppId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGoogleAppId]);

  const googleAppOptions: SelectOption[] = (apps ?? []).filter((a) => a.type === AppType.GOOGLE).map((a) => ({ value: String(a.id), label: a.name }));

  const googleLocationOptions: SelectOption[] = currentGoogleAppId
    ? (googleLocationsForApp[parseInt(currentGoogleAppId)] ?? []).map((place) => ({
        value: place.name,
        label: place.storefrontAddress?.addressLines?.[0] ? `${place.title} - ${place.storefrontAddress.addressLines[0]}` : place.title
      }))
    : [];

  const minRatingOptions: SelectOption[] = [
    { value: "1", label: "1 star & up" },
    { value: "2", label: "2 stars & up" },
    { value: "3", label: "3 stars & up" },
    { value: "4", label: "4 stars & up" },
    { value: "5", label: "5 stars only" }
  ];

  const handleEdit = (): void => {
    const seed: Partial<Record<LocationVarKey, string>> = {};
    for (const key of PAGE_KEYS) seed[key] = resolvedByKey[key]!.value;
    setDraft(seed);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft({});
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      for (const key of PAGE_KEYS) {
        const next = draft[key];
        const resolved = resolvedByKey[key]!;
        if (next === undefined || next === resolved.value) continue;
        if (resolved.ownRow) await API.LocationVars.updateVariable(orgId, resolved.ownRow.id, next);
        else await API.LocationVars.createVariable(key, next, orgId, groupId, locationId);
      }
      await onSaved();
      setDraft({});
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (key: LocationVarKey): Promise<void> => {
    const resolved = resolvedByKey[key]!;
    if (!resolved.ownRow) return;
    if (!window.confirm("Revert? This discards the customization at this scope.")) return;
    setSaving(true);
    setError(null);
    try {
      await API.LocationVars.deleteVariable(orgId, resolved.ownRow.id);
      await onSaved();
      setDraft((prev) => {
        const { [key]: _dropped, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const revertFor = (key: LocationVarKey): { label: string; revertsTo: string; onRevert: () => void } | undefined => {
    const resolved = resolvedByKey[key]!;
    const ownsHere = resolved.ownerScope === scopeKind;
    if (!ownsHere) return undefined;
    const label = revertLabelFor({
      isOrgScope: scopeKind === "org",
      isOverride: ownsHere,
      inheritance: inheritanceFor(resolved.ownerScope, scopeKind),
      parentInheritance: inheritanceFor(resolved.parentScope, scopeKind)
    });
    return { label, revertsTo: resolved.parentValue || "(empty)", onRevert: () => void handleRevert(key) };
  };

  return (
    <SettingsCard
      title="Google Reviews"
      caption="Whether swivl syncs reviews from Google and replies to them automatically."
      isEditing={isEditing}
      saving={saving}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}

      <SettingRow
        label="Sync Google Reviews"
        description={`Populates your Google Reviews into swivl's Reviews section.`}
        inheritance={inheritanceFor(resolvedByKey[LocationVarKey.GoogleReviewsSyncEnabled]!.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={revertFor(LocationVarKey.GoogleReviewsSyncEnabled)}
      >
        <div className="flex items-center">
          <WidgetSwitch
            on={isTruthy(display(LocationVarKey.GoogleReviewsSyncEnabled))}
            disabled={!isEditing}
            onSet={(on) => setDraft((prev) => ({ ...prev, [LocationVarKey.GoogleReviewsSyncEnabled]: on ? "1" : "0" }))}
            ariaLabel="Sync Google Reviews"
          />
        </div>
      </SettingRow>

      <SettingRow
        label="Google Review App"
        description="The Google Business Profile app connected for this facility's reviews."
        inheritance={inheritanceFor(resolvedByKey[LocationVarKey.GoogleAppId]!.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={revertFor(LocationVarKey.GoogleAppId)}
      >
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">
            {googleAppOptions.find((o) => o.value === display(LocationVarKey.GoogleAppId))?.label || display(LocationVarKey.GoogleAppId) || "—"}
          </span>
        ) : (
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            selectedValues={[display(LocationVarKey.GoogleAppId)]}
            options={googleAppOptions}
            onChange={(values) => {
              if (values[0] !== undefined) setDraft((prev) => ({ ...prev, [LocationVarKey.GoogleAppId]: values[0] }));
            }}
          />
        )}
      </SettingRow>

      <SettingRow
        label="Google Review Place ID"
        description="The specific Google Business Profile location to sync reviews from."
        inheritance={inheritanceFor(resolvedByKey[LocationVarKey.GoogleAccountAndLocationName]!.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={revertFor(LocationVarKey.GoogleAccountAndLocationName)}
      >
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">
            {googleLocationOptions.find((o) => o.value === display(LocationVarKey.GoogleAccountAndLocationName))?.label ||
              display(LocationVarKey.GoogleAccountAndLocationName) ||
              "—"}
          </span>
        ) : (
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            selectedValues={[display(LocationVarKey.GoogleAccountAndLocationName)]}
            options={googleLocationOptions}
            onChange={(values) => {
              if (values[0] !== undefined) setDraft((prev) => ({ ...prev, [LocationVarKey.GoogleAccountAndLocationName]: values[0] }));
            }}
          />
        )}
      </SettingRow>

      <SettingRow
        label="Auto-Reply to Reviews"
        description="When ON, the bot automatically posts an AI-generated reply to new reviews meeting the minimum rating below."
        inheritance={inheritanceFor(resolvedByKey[LocationVarKey.GoogleReviewsAutoReplyEnabled]!.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={revertFor(LocationVarKey.GoogleReviewsAutoReplyEnabled)}
      >
        <div className="flex items-center">
          <WidgetSwitch
            on={isTruthy(display(LocationVarKey.GoogleReviewsAutoReplyEnabled))}
            disabled={!isEditing}
            onSet={(on) => setDraft((prev) => ({ ...prev, [LocationVarKey.GoogleReviewsAutoReplyEnabled]: on ? "1" : "0" }))}
            ariaLabel="Auto-reply to reviews"
          />
        </div>
      </SettingRow>

      <SettingRow
        label="Minimum Rating for Auto-Reply"
        description="Only auto-reply to reviews at or above this star rating."
        inheritance={inheritanceFor(resolvedByKey[LocationVarKey.GoogleReviewsAutoReplyMinRating]!.ownerScope, scopeKind)}
        isEditing={isEditing}
        revert={revertFor(LocationVarKey.GoogleReviewsAutoReplyMinRating)}
      >
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">
            {minRatingOptions.find((o) => o.value === display(LocationVarKey.GoogleReviewsAutoReplyMinRating))?.label ||
              display(LocationVarKey.GoogleReviewsAutoReplyMinRating) ||
              "—"}
          </span>
        ) : (
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            selectedValues={[display(LocationVarKey.GoogleReviewsAutoReplyMinRating)]}
            options={minRatingOptions}
            onChange={(values) => {
              if (values[0] !== undefined) setDraft((prev) => ({ ...prev, [LocationVarKey.GoogleReviewsAutoReplyMinRating]: values[0] }));
            }}
          />
        )}
      </SettingRow>
    </SettingsCard>
  );
}
